import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import type {
  RelationshipHistoryEntry,
  SpousePartnerEdge,
} from '@/lib/familyUnits';

import {
  evaluateCanonicalTreeExternalEvidenceGatewayContract,
  type CanonicalTreeExternalEvidenceGatewayResult,
} from './CanonicalTreeExternalEvidenceGatewayContract';

export type CanonicalTreeExternalEvidenceSnapshotStatus =
  | 'UNAVAILABLE'
  | 'LOADING'
  | 'LOADED'
  | 'FAILED'
  | 'STALE';

export type CanonicalTreeExternalEvidenceRefreshReason =
  | 'INITIAL-GRAPH-LOAD'
  | 'GRAPH-OWNER-CHANGED'
  | 'MEMBERS-CHANGED'
  | 'SPOUSE-WRITE'
  | 'RELATIONSHIP-HISTORY-WRITE'
  | 'MANUAL-REFRESH';

export interface CanonicalTreeExternalEvidenceSnapshot {
  readonly status:
    CanonicalTreeExternalEvidenceSnapshotStatus;

  readonly ownerUserId:
    string | null;

  readonly memberIds:
    readonly string[];

  readonly spousePartnerEdges:
    readonly SpousePartnerEdge[];

  readonly relationshipHistory:
    readonly RelationshipHistoryEntry[];

  readonly contract:
    CanonicalTreeExternalEvidenceGatewayResult | null;

  readonly requestedAt:
    string | null;

  readonly completedAt:
    string | null;

  readonly refreshReason:
    CanonicalTreeExternalEvidenceRefreshReason | null;

  readonly error:
    string | null;

  readonly readyForGateway:
    boolean;
}

export interface CanonicalTreeExternalEvidenceSnapshotLoadInput {
  readonly ownerUserId:
    string;

  readonly members:
    readonly GraphMember[];

  readonly spousePartnerEdges:
    readonly SpousePartnerEdge[];

  readonly relationshipHistory:
    readonly RelationshipHistoryEntry[];

  readonly requestedAt:
    string;

  readonly completedAt:
    string;

  readonly refreshReason:
    CanonicalTreeExternalEvidenceRefreshReason;
}

const freezeRows = <T>(
  rows:
    readonly T[],
): readonly T[] =>
  Object.freeze([
    ...rows,
  ]);

const memberIdsFor = (
  members:
    readonly GraphMember[],
): readonly string[] =>
  Object.freeze(
    Array.from(
      new Set(
        members.map(
          member =>
            member.id,
        ),
      ),
    ).sort(),
  );

export const createUnavailableCanonicalTreeExternalEvidenceSnapshot = ():
  CanonicalTreeExternalEvidenceSnapshot =>
  Object.freeze({
    status:
      'UNAVAILABLE',

    ownerUserId:
      null,

    memberIds:
      Object.freeze([]),

    spousePartnerEdges:
      Object.freeze([]),

    relationshipHistory:
      Object.freeze([]),

    contract:
      null,

    requestedAt:
      null,

    completedAt:
      null,

    refreshReason:
      null,

    error:
      null,

    readyForGateway:
      false,
  });

export const beginCanonicalTreeExternalEvidenceSnapshotLoad = (
  input: {
    readonly ownerUserId:
      string;

    readonly members:
      readonly GraphMember[];

    readonly requestedAt:
      string;

    readonly refreshReason:
      CanonicalTreeExternalEvidenceRefreshReason;
  },
): CanonicalTreeExternalEvidenceSnapshot =>
  Object.freeze({
    status:
      'LOADING',

    ownerUserId:
      input.ownerUserId,

    memberIds:
      memberIdsFor(
        input.members,
      ),

    spousePartnerEdges:
      Object.freeze([]),

    relationshipHistory:
      Object.freeze([]),

    contract:
      null,

    requestedAt:
      input.requestedAt,

    completedAt:
      null,

    refreshReason:
      input.refreshReason,

    error:
      null,

    readyForGateway:
      false,
  });

export const completeCanonicalTreeExternalEvidenceSnapshotLoad = (
  input:
    CanonicalTreeExternalEvidenceSnapshotLoadInput,
): CanonicalTreeExternalEvidenceSnapshot => {
  const contract =
    evaluateCanonicalTreeExternalEvidenceGatewayContract({
      ownerUserId:
        input.ownerUserId,

      members:
        input.members,

      spousePartnerEdges:
        input.spousePartnerEdges,

      relationshipHistory:
        input.relationshipHistory,
    });

  return Object.freeze({
    status:
      contract.readyForGateway
        ? 'LOADED'
        : 'FAILED',

    ownerUserId:
      input.ownerUserId,

    memberIds:
      memberIdsFor(
        input.members,
      ),

    spousePartnerEdges:
      freezeRows(
        contract
          .spousePartnerEdges
          .accepted,
      ),

    relationshipHistory:
      freezeRows(
        contract
          .relationshipHistory
          .accepted,
      ),

    contract,

    requestedAt:
      input.requestedAt,

    completedAt:
      input.completedAt,

    refreshReason:
      input.refreshReason,

    error:
      contract.readyForGateway
        ? null
        : [
            ...contract
              .spousePartnerEdges
              .reasons,
            ...contract
              .relationshipHistory
              .reasons,
          ].join(' | ') ||
          'External relationship evidence failed gateway validation.',

    readyForGateway:
      contract.readyForGateway,
  });
};

export const failCanonicalTreeExternalEvidenceSnapshotLoad = (
  input: {
    readonly ownerUserId:
      string | null;

    readonly members:
      readonly GraphMember[];

    readonly requestedAt:
      string;

    readonly completedAt:
      string;

    readonly refreshReason:
      CanonicalTreeExternalEvidenceRefreshReason;

    readonly error:
      string;
  },
): CanonicalTreeExternalEvidenceSnapshot =>
  Object.freeze({
    status:
      'FAILED',

    ownerUserId:
      input.ownerUserId,

    memberIds:
      memberIdsFor(
        input.members,
      ),

    spousePartnerEdges:
      Object.freeze([]),

    relationshipHistory:
      Object.freeze([]),

    contract:
      null,

    requestedAt:
      input.requestedAt,

    completedAt:
      input.completedAt,

    refreshReason:
      input.refreshReason,

    error:
      input.error,

    readyForGateway:
      false,
  });

export const markCanonicalTreeExternalEvidenceSnapshotStale = (
  input: {
    readonly snapshot:
      CanonicalTreeExternalEvidenceSnapshot;

    readonly refreshReason:
      CanonicalTreeExternalEvidenceRefreshReason;
  },
): CanonicalTreeExternalEvidenceSnapshot =>
  Object.freeze({
    ...input.snapshot,

    status:
      'STALE',

    refreshReason:
      input.refreshReason,

    readyForGateway:
      false,
  });

export const canonicalTreeExternalEvidenceSnapshotMatchesGraph = (
  input: {
    readonly snapshot:
      CanonicalTreeExternalEvidenceSnapshot;

    readonly ownerUserId:
      string | null | undefined;

    readonly members:
      readonly GraphMember[];
  },
): boolean => {
  const ownerUserId =
    input.ownerUserId ?? null;

  const memberIds =
    memberIdsFor(
      input.members,
    );

  return (
    input.snapshot.status ===
      'LOADED' &&
    input.snapshot.readyForGateway &&
    input.snapshot.ownerUserId ===
      ownerUserId &&
    JSON.stringify(
      input.snapshot.memberIds,
    ) ===
      JSON.stringify(
        memberIds,
      )
  );
};
